import { useEffect, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import AppShell from '../app/AppShell';
import { getPortalRole } from '../app/businessContext';
import { FeedbackMessage } from '../components/common';
import {
  fetchPurchaseOrders,
  fetchSupplierBalancings,
  fetchSupplierSavingsReleases,
} from '../services/purchaseOrderService';
import { fetchReleasingWorkspace } from '../services/releasingService';
import { fetchLoans } from '../services/loanService';

const quickActions = [
  { id: 'add-po', label: 'Add P.O.', path: '/admin/purchase-order?tab=create' },
  { id: 'balancing', label: 'Supplier Balancing', path: '/admin/supplier-balancing' },
  { id: 'release', label: 'Release Inventory', path: '/admin/releasing?tab=pending' },
  { id: 'sales-order', label: 'Sales Order', path: '/admin/sales-order' },
];

function DashboardPage({ navigationItems, activePath, onNavigate }) {
  const { dashboardData, isLoading, loadError } = useDashboardData();

  return (
    <AppShell
      navigationItems={navigationItems}
      activePath={activePath}
      onNavigate={onNavigate}
    >
      <div className="space-y-5 px-4 py-5 sm:px-6 lg:px-[26px]">
        <DashboardHeader onNavigate={onNavigate} />
        {loadError ? (
          <FeedbackMessage tone="error" message={loadError} />
        ) : null}
        {isLoading ? (
          <FeedbackMessage message="Loading dashboard data." />
        ) : null}
        <DashboardOverview data={dashboardData} onNavigate={onNavigate} />
      </div>
    </AppShell>
  );
}

function DashboardHeader({ onNavigate }) {
  const visibleQuickActions = getDashboardQuickActions();

  return (
    <section className="rounded-[22px] border border-[var(--theme-border-soft)] bg-white px-5 py-5 shadow-[0_12px_30px_rgba(var(--theme-shadow-rgb),0.05)] sm:px-6">
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-end">
        <div>
          <p className="m-0 text-[0.74rem] font-semibold uppercase tracking-[0.16em] text-[var(--theme-text-subtle)]">
            Operations
          </p>
          <h1 className="m-0 mt-1 text-[1.55rem] font-semibold leading-tight text-[var(--theme-text)] sm:text-[1.9rem]">
            Dashboard
          </h1>
          <p className="m-0 mt-2 max-w-[760px] text-[0.94rem] leading-6 text-[var(--theme-text-muted)]">
            A calm daily view of the work that needs attention across buying, inventory,
            releasing, and finance.
          </p>
        </div>

        <div className="flex flex-wrap gap-2 xl:justify-end">
          {visibleQuickActions.map((action) => (
            <button
              key={action.id}
              type="button"
              className="inline-flex min-h-[42px] items-center gap-2 rounded-full border border-[var(--theme-border)] bg-white px-4 text-left text-[0.86rem] font-semibold text-[var(--theme-text-muted)] transition hover:bg-[var(--theme-surface-soft)] hover:text-[var(--theme-text)]"
              onClick={() => onNavigate(getQuickActionPath(action))}
            >
              <span>{action.label}</span>
              <ChevronRight aria-hidden="true" size={16} />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function getDashboardQuickActions() {
  if (getPortalRole() === 'admin-custom') {
    return quickActions.filter((action) => action.id === 'balancing');
  }

  return quickActions.filter((action) =>
    ['add-po', 'balancing', 'release'].includes(action.id)
  );
}

function DashboardOverview({ data, onNavigate }) {
  const headlineMetrics = data.todayMetrics.slice(0, 4);
  const isCustomAdmin = getPortalRole() === 'admin-custom';

  return (
    <div className="grid gap-5">
      <MetricGrid metrics={headlineMetrics} />

      <section className="grid gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)]">
        <Panel
          title="Priority Work"
          description="The shortest path to today's important actions."
        >
          <QueueList rows={data.operationalQueue} onNavigate={onNavigate} />
        </Panel>

        <Panel
          title="Inventory Flow"
          description="Received, available, and released stock at a glance."
          tone="filled"
        >
          <ProgressList items={data.inventoryFlow} contrast />
        </Panel>
      </section>

      <section className="grid gap-5 lg:grid-cols-3">
        <Panel title="Recent Releases">
          <CompactList rows={data.recentReleases.slice(0, 4)} />
          {!isCustomAdmin ? (
            <ActionFooter label="Open Releasing" onClick={() => onNavigate('/admin/releasing?tab=history')} />
          ) : null}
        </Panel>
        <Panel title="Supplier Balancing">
          <CompactList rows={data.balancingQueue.slice(0, 4)} />
          <ActionFooter
            label="Open Balancing"
            onClick={() => onNavigate(getSupplierBalancingDashboardPath())}
          />
        </Panel>
        <Panel title="Watch List" tone="warning">
          <CompactList rows={data.alertRows.slice(0, 4)} />
        </Panel>
      </section>
    </div>
  );
}

function MetricGrid({ metrics }) {
  return (
    <section
      className="grid gap-3 md:grid-cols-2 xl:grid-cols-4"
      aria-label="Dashboard metrics"
    >
      {metrics.map((metric) => (
        <MetricCard key={metric.id} metric={metric} />
      ))}
    </section>
  );
}

function MetricCard({ metric }) {
  const toneClassName = getMetricToneClassName(metric.tone);

  return (
    <article className={`min-h-[132px] rounded-[18px] border px-4 py-4 shadow-[0_12px_28px_rgba(var(--theme-shadow-rgb),0.06)] ${toneClassName}`}>
      <p className="m-0 text-[0.76rem] font-bold uppercase tracking-[0.14em]">{metric.label}</p>
      <strong className="mt-3 block text-[1.8rem] font-extrabold leading-tight text-[var(--theme-text)] sm:text-[2rem]">
        {metric.value}
      </strong>
      <p className="m-0 mt-2 text-[0.88rem] leading-5 text-[var(--theme-text-muted)]">{metric.detail}</p>
    </article>
  );
}

function Panel({ title, description, children, tone = 'default' }) {
  const toneClassName =
    tone === 'filled'
      ? 'border-[var(--theme-accent)] bg-[var(--theme-accent-panel)]'
      : tone === 'warning'
        ? 'border-[color-mix(in_srgb,var(--theme-accent)_42%,var(--theme-border))] bg-[color-mix(in_srgb,var(--theme-surface-warm)_82%,white)]'
        : 'border-[var(--theme-border-soft)] bg-white';
  const headingClassName =
    tone === 'filled' ? 'text-[var(--theme-accent-panel-text)]' : 'text-[var(--theme-text)]';
  const descriptionClassName =
    tone === 'filled'
      ? 'text-[color-mix(in_srgb,var(--theme-accent-panel-text)_82%,transparent)]'
      : 'text-[var(--theme-text-muted)]';

  return (
    <article className={`rounded-[22px] border p-4 shadow-[0_14px_34px_rgba(var(--theme-shadow-rgb),0.07)] sm:p-5 ${toneClassName}`}>
      <div className={description ? 'mb-4' : 'mb-3'}>
        <h2 className={`m-0 text-[1.1rem] font-bold sm:text-[1.2rem] ${headingClassName}`}>{title}</h2>
        {description ? (
          <p className={`m-0 mt-1 text-[0.92rem] leading-6 ${descriptionClassName}`}>{description}</p>
        ) : null}
      </div>
      {children}
    </article>
  );
}

function QueueList({ rows, onNavigate }) {
  const pageSize = 4;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const normalizedPage = Math.min(currentPage, totalPages);
  const startIndex = (normalizedPage - 1) * pageSize;
  const visibleRows = rows.slice(startIndex, startIndex + pageSize);
  const shouldShowPagination = totalPages > 1;

  useEffect(() => {
    setCurrentPage(1);
  }, [rows]);

  return (
    <div className="overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)]">
      <div className="hidden grid-cols-[110px_minmax(0,1fr)_130px_92px] gap-4 bg-[var(--theme-accent-panel)] px-4 py-3 text-[0.72rem] font-extrabold uppercase tracking-[0.13em] text-[var(--theme-accent-panel-text)] lg:grid">
        <span>Priority</span>
        <span>Work Item</span>
        <span>Status</span>
        <span>Action</span>
      </div>

      {visibleRows.map((row) => (
        <div
          key={row.id}
          className="grid gap-3 border-t border-[var(--theme-border-soft)] bg-white px-4 py-4 first:border-t-0 lg:grid-cols-[110px_minmax(0,1fr)_130px_92px] lg:items-center"
        >
          <StatusBadge label={row.priority || row.status} tone={row.tone} />
          <div className="min-w-0">
            <p className="m-0 font-bold text-[var(--theme-text)]">{row.label || row.workItem}</p>
            <p className="m-0 mt-1 text-[0.88rem] leading-5 text-[var(--theme-text-muted)]">{row.detail || row.context}</p>
          </div>
          <StatusBadge label={row.status} tone={row.tone} />
          {row.path && !row.hideAction ? (
            <button
              type="button"
              className="inline-flex min-h-[38px] items-center justify-center rounded-[14px] border border-[var(--theme-accent)] bg-[var(--theme-accent)] px-4 text-[0.86rem] font-bold text-white transition hover:bg-[var(--theme-accent-strong)]"
              onClick={() => onNavigate(row.path)}
            >
              Open
            </button>
          ) : (
            <span className="hidden text-center text-[0.82rem] font-medium text-[var(--theme-text-subtle)] lg:block">
              -
            </span>
          )}
        </div>
      ))}
      {shouldShowPagination ? (
        <div className="flex items-center justify-between gap-3 border-t border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] px-4 py-2.5">
          <span className="text-[0.78rem] font-medium text-[var(--theme-text-muted)]">
            {startIndex + 1}-{Math.min(startIndex + pageSize, rows.length)} of {rows.length}
          </span>
          <div className="inline-flex items-center gap-1.5">
            <button
              type="button"
              className="grid h-8 w-8 place-items-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-accent-soft)] disabled:cursor-not-allowed disabled:opacity-45"
              disabled={normalizedPage <= 1}
              onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
              aria-label="Previous priority work page"
            >
              <ChevronRight className="rotate-180" aria-hidden="true" size={15} />
            </button>
            <span className="min-w-[42px] text-center text-[0.78rem] font-semibold text-[var(--theme-text-subtle)]">
              {normalizedPage}/{totalPages}
            </span>
            <button
              type="button"
              className="grid h-8 w-8 place-items-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-accent-soft)] disabled:cursor-not-allowed disabled:opacity-45"
              disabled={normalizedPage >= totalPages}
              onClick={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
              aria-label="Next priority work page"
            >
              <ChevronRight aria-hidden="true" size={15} />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CompactList({ rows }) {
  return (
    <div className="divide-y divide-[var(--theme-border-soft)]">
      {rows.map((row) => (
        <div key={row.id} className="grid gap-2 py-3 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
          <div className="min-w-0">
            <p className="m-0 font-bold text-[var(--theme-text)]">{row.label}</p>
            <p className="m-0 mt-1 text-[0.88rem] leading-5 text-[var(--theme-text-muted)]">{row.detail}</p>
          </div>
          <span className="inline-flex w-fit rounded-full border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] px-3 py-1 text-[0.8rem] font-semibold text-[var(--theme-text-subtle)]">
            {row.value || row.status}
          </span>
        </div>
      ))}
    </div>
  );
}

function ProgressList({ items, contrast = false }) {
  const labelClassName = contrast
    ? 'text-[var(--theme-accent-panel-text)]'
    : 'text-[var(--theme-text)]';
  const valueClassName = contrast
    ? 'text-[var(--theme-accent-panel-text)]'
    : 'text-[var(--theme-text-subtle)]';
  const trackClassName = contrast
    ? 'bg-[color-mix(in_srgb,var(--theme-accent-panel-text)_20%,transparent)]'
    : 'bg-[color-mix(in_srgb,var(--theme-accent-soft)_54%,white)]';
  const barClassName = contrast
    ? 'bg-[var(--theme-accent-panel-text)]'
    : 'bg-[var(--theme-accent)]';

  return (
    <div className="space-y-5">
      {items.map((item) => (
        <div key={item.id}>
          <div className="mb-2 flex items-center justify-between gap-3 text-[0.92rem]">
            <span className={`font-bold ${labelClassName}`}>{item.label}</span>
            <span className={`text-right font-bold ${valueClassName}`}>{item.value}</span>
          </div>
          <div className={`h-3 rounded-full ${trackClassName}`}>
            <div
              className={`h-full rounded-full ${barClassName}`}
              style={{ width: `${Math.max(0, Math.min(item.percent, 100))}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function ActionFooter({ label, onClick }) {
  return (
    <div className="mt-4 border-t border-[var(--theme-border-soft)] pt-4">
      <button
        type="button"
        className="inline-flex min-h-[42px] items-center gap-2 rounded-[15px] border border-[var(--theme-accent)] bg-[var(--theme-accent)] px-4 text-[0.9rem] font-bold text-white transition hover:bg-[var(--theme-accent-strong)]"
        onClick={onClick}
      >
        <span>{label}</span>
        <ChevronRight aria-hidden="true" size={17} />
      </button>
    </div>
  );
}

function StatusBadge({ label, tone = 'neutral' }) {
  const toneClassName =
    tone === 'success'
      ? 'border-[color-mix(in_srgb,var(--theme-accent)_48%,white)] bg-[color-mix(in_srgb,var(--theme-accent-soft)_62%,white)] text-[var(--theme-accent-strong)]'
      : tone === 'warning'
        ? 'border-[color-mix(in_srgb,var(--theme-accent)_32%,var(--theme-border))] bg-[color-mix(in_srgb,var(--theme-surface-warm)_74%,white)] text-[var(--theme-text-subtle)]'
        : tone === 'danger'
          ? 'border-[color-mix(in_srgb,var(--theme-border)_65%,var(--theme-text-muted))] bg-[color-mix(in_srgb,var(--theme-surface-soft)_72%,white)] text-[var(--theme-text)]'
          : tone === 'info'
            ? 'border-[color-mix(in_srgb,var(--theme-accent)_35%,var(--theme-border))] bg-[color-mix(in_srgb,var(--theme-accent-soft)_54%,white)] text-[var(--theme-text-subtle)]'
            : 'border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] text-[var(--theme-text-subtle)]';

  return (
    <span className={`inline-flex w-fit items-center rounded-full border px-3 py-1 text-[0.78rem] font-semibold leading-tight tracking-0 ${toneClassName}`}>
      {formatStatusLabel(label)}
    </span>
  );
}

function formatStatusLabel(label) {
  return String(label || '')
    .toLowerCase()
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

function getMetricToneClassName(tone = 'neutral') {
  if (tone === 'success') {
    return 'border-[color-mix(in_srgb,var(--theme-accent)_54%,white)] bg-[color-mix(in_srgb,var(--theme-accent-soft)_70%,white)] text-[var(--theme-accent-strong)]';
  }

  if (tone === 'warning') {
    return 'border-[color-mix(in_srgb,var(--theme-accent)_34%,var(--theme-border))] bg-[color-mix(in_srgb,var(--theme-surface-warm)_82%,white)] text-[var(--theme-text-subtle)]';
  }

  if (tone === 'info') {
    return 'border-[color-mix(in_srgb,var(--theme-accent)_42%,var(--theme-border))] bg-[color-mix(in_srgb,var(--theme-accent-soft)_58%,white)] text-[var(--theme-text-subtle)]';
  }

  return 'border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] text-[var(--theme-text-subtle)]';
}

function useDashboardData() {
  const [rawData, setRawData] = useState(createEmptyRawDashboardData);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function loadDashboardData() {
      setIsLoading(true);
      setLoadError('');

      try {
        const [
          purchaseOrders,
          supplierBalancings,
          supplierSavingsReleases,
          releasingWorkspace,
          loans,
        ] = await Promise.all([
          fetchPurchaseOrders(),
          fetchSupplierBalancings(),
          fetchSupplierSavingsReleases(),
          fetchReleasingWorkspace(),
          fetchLoans(),
        ]);

        if (!isMounted) {
          return;
        }

        setRawData({
          purchaseOrders: Array.isArray(purchaseOrders) ? purchaseOrders : [],
          supplierBalancings: Array.isArray(supplierBalancings) ? supplierBalancings : [],
          supplierSavingsReleases: Array.isArray(supplierSavingsReleases)
            ? supplierSavingsReleases
            : [],
          pendingSalesOrders: Array.isArray(releasingWorkspace?.pendingSalesOrders)
            ? releasingWorkspace.pendingSalesOrders
            : [],
          releaseHistory: Array.isArray(releasingWorkspace?.releaseHistory)
            ? releasingWorkspace.releaseHistory
            : [],
          availableInventory: Array.isArray(releasingWorkspace?.availableInventory)
            ? releasingWorkspace.availableInventory
            : [],
          loans: Array.isArray(loans) ? loans : [],
        });
      } catch (error) {
        if (isMounted) {
          setLoadError(error.message || 'Unable to load dashboard data right now.');
          setRawData(createEmptyRawDashboardData());
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadDashboardData();

    return () => {
      isMounted = false;
    };
  }, []);

  const dashboardData = useMemo(() => buildDashboardData(rawData), [rawData]);

  return {
    dashboardData,
    isLoading,
    loadError,
  };
}

function buildDashboardData(rawData) {
  const purchaseOrders = rawData.purchaseOrders || [];
  const supplierBalancings = rawData.supplierBalancings || [];
  const supplierSavingsReleases = rawData.supplierSavingsReleases || [];
  const pendingSalesOrders = rawData.pendingSalesOrders || [];
  const releaseHistory = rawData.releaseHistory || [];
  const availableInventory = rawData.availableInventory || [];
  const loans = rawData.loans || [];

  const activePurchaseOrders = purchaseOrders.filter((purchaseOrder) =>
    ['Initial', 'In Progress', 'Received'].includes(String(purchaseOrder.status || ''))
  );
  const activeBalancingPurchaseOrderIds = new Set(
    supplierBalancings
      .filter((record) => ['For Payment', 'Paid'].includes(String(record.status || '')))
      .flatMap((record) => record.purchaseOrderIds || [])
      .map((purchaseOrderId) => String(purchaseOrderId))
  );
  const pendingBalancingPurchaseOrders = purchaseOrders.filter((purchaseOrder) =>
    isPendingBalancingPurchaseOrder(purchaseOrder, activeBalancingPurchaseOrderIds)
  );
  const completedInventory = availableInventory.filter((item) => isCompletedInventoryItem(item));
  const availableKg = sumBy(completedInventory, (item) => item.availableKg);
  const availableCapital = sumBy(completedInventory, getInventoryCapital);
  const pendingReleaseFcl = sumBy(pendingSalesOrders, (salesOrder) => salesOrder.remainingQuantity);
  const todayReleasedKg = sumBy(
    releaseHistory.filter((release) => isTodayDate(release.releaseDate)),
    (release) => release.releasedKg
  );
  const releasedKg = sumBy(
    releaseHistory.filter((release) => String(release.status || '') !== 'Pre-Released'),
    (release) => release.releasedKg
  );
  const draftReleases = releaseHistory.filter(
    (release) => String(release.status || '') === 'Pre-Released'
  );
  const preReleaseCandidateCount = availableInventory.filter((item) => item.isPreReleaseCandidate).length;
  const openLoans = loans.filter((loan) => String(loan.status || '') === 'Open');
  const openCashAdvanceTotal = sumBy(openLoans, (loan) => loan.remainingAmount ?? loan.amount);
  const payableBalancings = supplierBalancings.filter((record) =>
    ['For Payment', 'Paid'].includes(String(record.status || ''))
  );
  const grossPayable = sumBy(payableBalancings, (record) => record.totals?.grossPayable);
  const totalBalance = sumBy(payableBalancings, (record) => record.totals?.totalBalance);
  const supplierSavings = sumBy(payableBalancings, (record) => record.totals?.savingsTotal);
  const releasedSavings = sumBy(supplierSavingsReleases, (record) => record.amount || record.totalAmount);
  const supplierExposure = totalBalance + openCashAdvanceTotal + availableCapital;
  const purchaseStatusCounts = getCountsBy(purchaseOrders, (purchaseOrder) => purchaseOrder.status || 'Unknown');
  const warehouseGroups = groupBy(completedInventory, (item) =>
    String(item.location || item.warehouseLocation || item.branch || 'Unassigned').trim() || 'Unassigned'
  );

  return {
    todayMetrics: [
      {
        id: 'pending-po',
        label: 'Pending P.O.',
        value: formatWhole(activePurchaseOrders.length),
        detail: 'Initial, in-progress, and received orders',
        tone: activePurchaseOrders.length ? 'warning' : 'success',
      },
      {
        id: 'pending-balancing',
        label: 'Pending Balancing',
        value: formatWhole(pendingBalancingPurchaseOrders.length),
        detail: 'Received or completed P.O.s ready for balancing',
        tone: pendingBalancingPurchaseOrders.length ? 'warning' : 'success',
      },
      {
        id: 'available-inventory',
        label: 'Available Inventory',
        value: formatKg(availableKg),
        detail: 'Completed stock ready for release allocation',
        tone: availableKg > 0 ? 'success' : 'neutral',
      },
      {
        id: 'pending-release',
        label: 'Pending Release',
        value: `${formatWhole(pendingReleaseFcl)} FCL`,
        detail: 'Sales order slots still open',
        tone: pendingReleaseFcl ? 'warning' : 'success',
      },
      {
        id: 'released-today',
        label: 'Released Today',
        value: formatKg(todayReleasedKg),
        detail: 'Confirmed release history dated today',
        tone: 'neutral',
      },
      {
        id: 'supplier-exposure',
        label: 'Supplier Exposure',
        value: formatCompactCurrency(supplierExposure),
        detail: 'Inventory capital, payable balance, and open C.A.',
        tone: supplierExposure ? 'info' : 'neutral',
      },
    ],
    operationalQueue: buildOperationalQueue({
      pendingBalancingPurchaseOrders,
      supplierBalancings,
      pendingSalesOrders,
      draftReleases,
      openLoans,
    }),
    inventoryFlow: buildInventoryFlow({
      purchaseOrders,
      availableKg,
      releasedKg,
    }),
    recentReleases: withFallbackRows(
      releaseHistory
        .filter((release) => !isPreReleasedRecord(release))
        .sort((first, second) => compareDateDesc(first.releaseDate, second.releaseDate))
        .slice(0, 3)
        .map((release) => ({
          id: `release-${release.id || getReleaseCode(release)}`,
          label: getReleaseCode(release),
          detail: `${release.batchNo || '-'} - ${formatKg(release.releasedKg)}`,
          status: release.status || 'Released',
        })),
      'No release history yet.'
    ),
    balancingQueue: withFallbackRows(
      pendingBalancingPurchaseOrders.slice(0, 3).map((purchaseOrder) => ({
        id: `balancing-${purchaseOrder.id}`,
        label: formatPurchaseOrderCode(purchaseOrder),
        detail: `${purchaseOrder.supplierName || 'Supplier'} - ${formatQuantity(purchaseOrder.quantity, purchaseOrder.stockUnit)}`,
        status: purchaseOrder.status || 'Ready',
      })),
      'No pending balancing P.O.s.'
    ),
    agingInventory: buildAgingInventoryRows(completedInventory, draftReleases),
    inventoryStages: [
      {
        id: 'receiving',
        label: 'Receiving',
        value: `${formatWhole((purchaseStatusCounts.get('In Progress') || 0) + (purchaseStatusCounts.get('Received') || 0))} P.O.`,
        detail: 'Orders still moving through receiving',
      },
      {
        id: 'dumping',
        label: 'Completed P.O.',
        value: formatWhole(purchaseStatusCounts.get('Completed') || 0),
        detail: 'Balanced stock available to inventory',
      },
      {
        id: 'warehouse',
        label: 'Warehouse',
        value: formatKg(availableKg),
        detail: 'Completed available stock',
      },
      {
        id: 'release',
        label: 'Release',
        value: `${formatWhole(pendingReleaseFcl)} FCL`,
        detail: 'Pending allocation to active sales orders',
      },
    ],
    warehouseMix: withFallbackProgressRows(
      Array.from(warehouseGroups.entries())
        .map(([location, rows]) => ({
          id: `warehouse-${location}`,
          label: location,
          kg: sumBy(rows, (item) => item.availableKg),
        }))
        .sort((first, second) => second.kg - first.kg)
        .slice(0, 4)
        .map((row) => ({
          id: row.id,
          label: row.label,
          value: formatKg(row.kg),
          percent: availableKg > 0 ? (row.kg / availableKg) * 100 : 0,
        })),
      'No completed inventory yet.'
    ),
    movementHealth: [
      {
        id: 'flow-1',
        label: 'Receiving to balancing',
        detail: `${formatWhole(pendingBalancingPurchaseOrders.length)} P.O.s ready for balancing`,
        status: pendingBalancingPurchaseOrders.length ? 'Active' : 'Clear',
      },
      {
        id: 'flow-2',
        label: 'Completed inventory',
        detail: `${formatKg(availableKg)} ready for final release selection`,
        status: availableKg > 0 ? 'Ready' : 'Empty',
      },
      {
        id: 'flow-3',
        label: 'Draft release watch',
        detail: `${formatWhole(draftReleases.length)} saved draft release${draftReleases.length === 1 ? '' : 's'}`,
        status: draftReleases.length ? 'Review' : 'Clear',
      },
    ],
    purchasePipeline: [
      { id: 'initial', label: 'Initial P.O.', value: formatWhole(purchaseStatusCounts.get('Initial') || 0), detail: 'Generated but not yet received' },
      { id: 'in-progress', label: 'In Progress', value: formatWhole(purchaseStatusCounts.get('In Progress') || 0), detail: 'Editable and receiving active' },
      { id: 'received', label: 'Received', value: formatWhole(purchaseStatusCounts.get('Received') || 0), detail: 'Ready to finalize for balancing' },
      { id: 'completed', label: 'Completed', value: formatWhole(purchaseStatusCounts.get('Completed') || 0), detail: 'Balanced and available to inventory' },
    ],
    supplierFinanceRows: buildSupplierFinanceRows(payableBalancings, openLoans),
    salesReleaseMetrics: [
      { id: 'pending-so', label: 'Pending S.O.', value: formatWhole(pendingSalesOrders.length), detail: 'Sales orders with remaining FCL' },
      { id: 'partial', label: 'Partially Released', value: formatWhole(pendingSalesOrders.filter((order) => String(order.status || '') === 'Partially Released').length), detail: 'Batches with release history' },
      { id: 'drafts', label: 'Draft Releases', value: formatWhole(draftReleases.length), detail: 'Saved release allocations' },
      { id: 'pre-release', label: 'Pre-release P.O.', value: formatWhole(preReleaseCandidateCount), detail: 'Needs completed balancing first' },
    ],
    salesReleaseRows: withFallbackRows(
      pendingSalesOrders.slice(0, 4).map((salesOrder) => ({
        id: `sales-${salesOrder.id || salesOrder.batchNo}`,
        label: salesOrder.batchNo || salesOrder.batchNumber || '-',
        detail: `${formatWhole(salesOrder.releasedQuantity || 0)} of ${formatWhole(salesOrder.quantity || salesOrder.requestedQuantity || 0)} FCL released`,
        value: `${formatWhole(salesOrder.remainingQuantity || 0)} FCL remaining`,
      })),
      'No pending sales orders.'
    ),
    releaseGuardrails: [
      { id: 'guard-1', label: 'Pre-release selected', detail: `${formatWhole(preReleaseCandidateCount)} available candidate${preReleaseCandidateCount === 1 ? '' : 's'}`, status: preReleaseCandidateCount ? 'Guard' : 'Clear' },
      { id: 'guard-2', label: 'Net kilo readiness', detail: `${formatWhole(pendingReleaseFcl)} FCL still pending release`, status: pendingReleaseFcl ? 'Required' : 'Clear' },
      { id: 'guard-3', label: 'Draft allocation', detail: `${formatWhole(draftReleases.length)} draft${draftReleases.length === 1 ? '' : 's'} can be edited before final release`, status: draftReleases.length ? 'Editable' : 'Clear' },
    ],
    financeMetrics: [
      { id: 'gross-payable', label: 'Gross Payable', value: formatCompactCurrency(grossPayable), detail: 'Supplier balancing gross payable' },
      { id: 'cash-advance', label: 'Cash Advances', value: formatCompactCurrency(openCashAdvanceTotal), detail: 'Open supplier C.A. remaining' },
      { id: 'savings', label: 'Supplier Savings', value: formatCompactCurrency(supplierSavings - releasedSavings), detail: 'Savings held after releases' },
      { id: 'capital', label: 'Inventory Capital', value: formatCompactCurrency(availableCapital), detail: 'Completed available inventory value' },
    ],
    cashAdvanceRows: withFallbackRows(
      openLoans.slice(0, 3).map((loan) => ({
        id: `loan-${loan.id}`,
        label: loan.referenceNo || loan.loanNo || loan.supplierName || `C.A. ${loan.id}`,
        detail: loan.supplierName || loan.remarks || 'Open supplier cash advance',
        status: formatCurrency(loan.remainingAmount ?? loan.amount),
      })),
      'No open cash advances.'
    ),
    alertRows: buildAlertRows({
      pendingBalancingPurchaseOrders,
      draftReleases,
      preReleaseCandidateCount,
      openLoans,
      completedInventory,
    }),
    controlSummary: [
      { id: 'control-1', label: 'Pre-release guard', detail: 'Final release blocked until balancing', status: 'On' },
      { id: 'control-2', label: 'Draft release edits', detail: 'Allocations stay editable before final release', status: 'On' },
      { id: 'control-3', label: 'Cancel P.O. guard', detail: 'Tagged purchase orders cannot be cancelled', status: 'On' },
    ],
  };
}

function createEmptyRawDashboardData() {
  return {
    purchaseOrders: [],
    supplierBalancings: [],
    supplierSavingsReleases: [],
    pendingSalesOrders: [],
    releaseHistory: [],
    availableInventory: [],
    loans: [],
  };
}

function buildOperationalQueue({
  pendingBalancingPurchaseOrders,
  supplierBalancings,
  pendingSalesOrders,
  openLoans,
}) {
  const shouldHideRestrictedAdminActions = getPortalRole() === 'admin-custom';
  const balanceRows = pendingBalancingPurchaseOrders.slice(0, 2).map((purchaseOrder) => ({
      id: `queue-balance-${purchaseOrder.id}`,
      priority: 'Balance',
      label: `${formatPurchaseOrderCode(purchaseOrder)} Supplier Balancing`,
      detail: `${purchaseOrder.supplierName || 'Supplier'} - ${formatQuantity(purchaseOrder.quantity, purchaseOrder.stockUnit)}`,
      status: purchaseOrder.status || 'Ready',
      tone: 'warning',
      path: buildSupplierBalancingPath(purchaseOrder),
    }));
  const releaseRows = pendingSalesOrders
    .filter((salesOrder) => Number(salesOrder.remainingQuantity || 0) > 0)
    .slice(0, 2)
    .map((salesOrder) => {
      const batchNo = getSalesOrderBatchNo(salesOrder);

      return {
        id: `queue-release-${salesOrder.id || batchNo}`,
        priority: 'Release',
        label: `${batchNo} Release Ready`,
        detail: `${batchNo} - ${formatWhole(salesOrder.remainingQuantity || 0)} FCL remaining for ${salesOrder.customer || salesOrder.customerName || 'customer'}`,
        status: salesOrder.status || 'Pending',
        tone: 'success',
        path: buildReleasingBatchPath(salesOrder),
        hideAction: shouldHideRestrictedAdminActions,
      };
    });
  const cashAdvanceRows = openLoans.slice(0, 1).map((loan) => ({
      id: `queue-loan-${loan.id}`,
      priority: 'C.A.',
      label: `${loan.supplierName || 'Supplier'} Cash Advance`,
      detail: `${formatCurrency(loan.remainingAmount ?? loan.amount)} remaining`,
      status: loan.status || 'Open',
      tone: 'danger',
      path: buildCashAdvancePath(loan),
      hideAction: shouldHideRestrictedAdminActions,
    }));
  const forPaymentBalancingRows = supplierBalancings
    .filter((record) => String(record.status || '') === 'For Payment')
    .slice(0, 1)
    .map((record) => ({
      id: `queue-payment-${record.id}`,
      priority: 'Payment',
      label: `${record.supplierName || record.sacksMark || 'Supplier'} Balancing Payment`,
      detail: `${formatCurrency(record.totals?.totalBalance)} payable`,
      status: 'For Payment',
      tone: 'warning',
      path: buildSupplierBalancingPaymentPath(record),
    }));
  const rows = [
    ...balanceRows.slice(0, 2),
    ...forPaymentBalancingRows,
    ...releaseRows.slice(0, 1),
    ...cashAdvanceRows,
    ...releaseRows.slice(1),
  ];

  return rows.length
    ? rows
    : [
        {
          id: 'queue-empty',
          priority: 'Clear',
          label: 'No urgent operations',
          detail: 'There are no pending dashboard actions right now.',
          status: 'Clear',
          tone: 'success',
        },
      ];
}

function buildSupplierBalancingPath(purchaseOrder) {
  const searchParams = new URLSearchParams();
  const supplierId = String(purchaseOrder?.supplierId || '').trim();
  const basePath = getSupplierBalancingBasePath();

  if (basePath === '/admin/purchase-order') {
    searchParams.set('tab', 'supplier-balancing');
  }
  if (supplierId) {
    searchParams.set('supplierId', supplierId);
  }

  return `${basePath}${searchParams.toString() ? `?${searchParams}` : ''}`;
}

function buildSupplierBalancingPaymentPath(record) {
  const searchParams = new URLSearchParams();
  const basePath = getSupplierBalancingBasePath();
  const recordId = String(record?.id || '').trim();

  if (basePath === '/admin/purchase-order') {
    searchParams.set('tab', 'supplier-balancing');
  }

  if (recordId) {
    searchParams.set('savedBalancingId', recordId);
  }

  return `${basePath}${searchParams.toString() ? `?${searchParams}` : ''}`;
}

function getQuickActionPath(action) {
  if (action.id === 'balancing') {
    return getSupplierBalancingDashboardPath();
  }

  return action.path;
}

function getSupplierBalancingDashboardPath() {
  const basePath = getSupplierBalancingBasePath();

  if (basePath === '/admin/purchase-order') {
    return `${basePath}?tab=supplier-balancing`;
  }

  return basePath;
}

function getSupplierBalancingBasePath() {
  return getPortalRole() === 'admin-custom'
    ? '/admin/supplier-balancing'
    : '/admin/purchase-order';
}

function buildReleasingBatchPath(salesOrder) {
  const searchParams = new URLSearchParams({ tab: 'pending' });
  const batchNo = getSalesOrderBatchNo(salesOrder);

  if (batchNo && batchNo !== 'Batch') {
    searchParams.set('batch', batchNo);
  }

  return `/admin/releasing?${searchParams}`;
}

function buildCashAdvancePath(loan) {
  const searchParams = new URLSearchParams();
  const loanId = String(loan?.id || '').trim();
  const supplierName = String(loan?.supplierName || '').trim();

  if (loanId) {
    searchParams.set('caId', loanId);
  }

  if (supplierName) {
    searchParams.set('search', supplierName);
  }

  return `/admin/cash-advance${searchParams.toString() ? `?${searchParams}` : ''}`;
}

function getSalesOrderBatchNo(salesOrder) {
  return salesOrder?.soCode || salesOrder?.batchNo || salesOrder?.batchNumber || 'Batch';
}

function buildInventoryFlow({ purchaseOrders, availableKg, releasedKg }) {
  const receivedKg = sumBy(purchaseOrders, getPurchaseOrderReceivedKg);
  const maxKg = Math.max(receivedKg, availableKg, releasedKg, 1);

  return [
    { id: 'received', label: 'Received', value: formatKg(receivedKg), percent: (receivedKg / maxKg) * 100 },
    { id: 'completed', label: 'Completed', value: formatKg(availableKg + releasedKg), percent: ((availableKg + releasedKg) / maxKg) * 100 },
    { id: 'available', label: 'Available', value: formatKg(availableKg), percent: (availableKg / maxKg) * 100 },
    { id: 'released', label: 'Released', value: formatKg(releasedKg), percent: (releasedKg / maxKg) * 100 },
  ];
}

function buildAgingInventoryRows(completedInventory, draftReleases) {
  const now = new Date();
  const agedItems = completedInventory.filter((item) => {
    const receivedDate = new Date(item.receivedDate || item.receivedAt || item.poDate || 0);

    return !Number.isNaN(receivedDate.getTime()) && now.getTime() - receivedDate.getTime() >= 7 * 24 * 60 * 60 * 1000;
  });
  const rows = [
    agedItems.length
      ? {
          id: 'age-old-stock',
          label: `${formatWhole(agedItems.length)} source${agedItems.length === 1 ? '' : 's'}`,
          detail: 'Older than 7 days in available inventory',
          status: 'Watch',
        }
      : null,
    draftReleases.length
      ? {
          id: 'age-drafts',
          label: `${formatWhole(draftReleases.length)} draft release${draftReleases.length === 1 ? '' : 's'}`,
          detail: 'Saved allocation should be reviewed',
          status: 'Review',
        }
      : null,
    completedInventory.length
      ? {
          id: 'age-capital',
          label: formatCompactCurrency(sumBy(completedInventory, getInventoryCapital)),
          detail: 'Capital currently sitting in available inventory',
          status: 'Plan',
        }
      : null,
  ].filter(Boolean);

  return withFallbackRows(rows, 'No aging inventory signals.');
}

function buildSupplierFinanceRows(payableBalancings, openLoans) {
  const supplierRows = new Map();

  payableBalancings.forEach((record) => {
    const supplierName = record.supplierName || record.sacksMark || 'Supplier';
    const currentRow = supplierRows.get(supplierName) || {
      id: `supplier-${supplierName}`,
      label: supplierName,
      payable: 0,
      records: 0,
      openAdvance: 0,
    };

    currentRow.payable += Number(record.totals?.totalBalance || record.totals?.grossPayable || 0);
    currentRow.records += 1;
    supplierRows.set(supplierName, currentRow);
  });

  openLoans.forEach((loan) => {
    const supplierName = loan.supplierName || 'Supplier';
    const currentRow = supplierRows.get(supplierName) || {
      id: `supplier-${supplierName}`,
      label: supplierName,
      payable: 0,
      records: 0,
      openAdvance: 0,
    };

    currentRow.openAdvance += Number(loan.remainingAmount ?? loan.amount ?? 0);
    supplierRows.set(supplierName, currentRow);
  });

  return withFallbackRows(
    Array.from(supplierRows.values())
      .sort((first, second) => second.payable + second.openAdvance - (first.payable + first.openAdvance))
      .slice(0, 3)
      .map((row) => ({
        id: row.id,
        label: row.label,
        detail: `${formatWhole(row.records)} balancing record${row.records === 1 ? '' : 's'} · ${formatCurrency(row.openAdvance)} open C.A.`,
        value: formatCompactCurrency(row.payable + row.openAdvance),
      })),
    'No supplier finance exposure yet.'
  );
}

function buildAlertRows({
  pendingBalancingPurchaseOrders,
  draftReleases,
  preReleaseCandidateCount,
  openLoans,
  completedInventory,
}) {
  const rows = [];

  if (preReleaseCandidateCount > 0) {
    rows.push({
      id: 'pre-release',
      label: 'Pre-release P.O.s need final balancing',
      detail: `${formatWhole(preReleaseCandidateCount)} candidate${preReleaseCandidateCount === 1 ? '' : 's'} cannot be finally released yet.`,
      status: 'Guardrail',
      tone: 'warning',
      path: '/admin/releasing',
    });
  }

  if (draftReleases.length > 0) {
    rows.push({
      id: 'draft-release',
      label: 'Draft release allocations should be reviewed',
      detail: `${formatWhole(draftReleases.length)} saved draft${draftReleases.length === 1 ? '' : 's'} found.`,
      status: 'Review',
      tone: 'info',
      path: '/admin/releasing',
    });
  }

  if (pendingBalancingPurchaseOrders.length > 0) {
    rows.push({
      id: 'pending-balancing',
      label: 'P.O.s ready for supplier balancing',
      detail: `${formatWhole(pendingBalancingPurchaseOrders.length)} purchase order${pendingBalancingPurchaseOrders.length === 1 ? '' : 's'} waiting.`,
      status: 'Attention',
      tone: 'danger',
      path: '/admin/supplier-balancing',
    });
  }

  if (openLoans.length > 0) {
    rows.push({
      id: 'cash-advance',
      label: 'Open supplier cash advances',
      detail: `${formatCompactCurrency(sumBy(openLoans, (loan) => loan.remainingAmount ?? loan.amount))} remaining.`,
      status: 'Finance',
      tone: 'warning',
      path: '/admin/cash-advance',
    });
  }

  if (!rows.length && completedInventory.length > 0) {
    rows.push({
      id: 'inventory-clear',
      label: 'Inventory is ready',
      detail: `${formatKg(sumBy(completedInventory, (item) => item.availableKg))} available for release allocation.`,
      status: 'Ready',
      tone: 'success',
      path: '/admin/releasing',
    });
  }

  return rows.length
    ? rows
    : [
        {
          id: 'alerts-empty',
          label: 'No dashboard alerts',
          detail: 'Operations look clear based on current records.',
          status: 'Clear',
          tone: 'success',
        },
      ];
}

function isPendingBalancingPurchaseOrder(purchaseOrder, activeBalancingPurchaseOrderIds = new Set()) {
  const status = String(purchaseOrder.status || '');

  return (
    purchaseOrder.stockUnit === 'sacks' &&
    ['Received', 'Completed'].includes(status) &&
    (status !== 'Received' || Boolean(purchaseOrder.balancingReadyAt)) &&
    !activeBalancingPurchaseOrderIds.has(String(purchaseOrder.id))
  );
}

function isCompletedInventoryItem(item) {
  const sourceType = String(item.sourceType || '').trim().toLowerCase();
  const isMergedInventory = sourceType === 'merge' || Number(item.mergeId || 0) > 0;

  return (
    !item.isPreReleaseCandidate &&
    (isMergedInventory || String(item.poStatus || '').trim() === 'Completed')
  );
}

function getPurchaseOrderReceivedKg(purchaseOrder) {
  if (!Array.isArray(purchaseOrder.receivingItems)) {
    return Number(purchaseOrder.netKg || purchaseOrder.receivedKg || 0);
  }

  return sumBy(purchaseOrder.receivingItems, (item) =>
    item.quantityUnit === 'sacks'
      ? item.rawNetKg || item.quantityKg || item.adjustedNetKg
      : item.quantityKg || item.quantity
  );
}

function getInventoryCapital(item) {
  const explicitCapital = Number(item.capital);

  if (Number.isFinite(explicitCapital) && explicitCapital > 0) {
    return explicitCapital;
  }

  return Number(item.availableKg || 0) * Number(item.unitCost || 0);
}

function getCountsBy(rows, getKey) {
  return rows.reduce((counts, row) => {
    const key = getKey(row);

    counts.set(key, (counts.get(key) || 0) + 1);
    return counts;
  }, new Map());
}

function groupBy(rows, getKey) {
  return rows.reduce((groups, row) => {
    const key = getKey(row);
    const group = groups.get(key) || [];

    group.push(row);
    groups.set(key, group);
    return groups;
  }, new Map());
}

function sumBy(rows, getValue) {
  return rows.reduce((total, row) => {
    const value = typeof getValue === 'function' ? getValue(row) : row?.[getValue];
    const numericValue = Number(value || 0);

    return Number.isFinite(numericValue) ? total + numericValue : total;
  }, 0);
}

function withFallbackRows(rows, fallbackDetail) {
  return rows.length
    ? rows
    : [
        {
          id: `empty-${fallbackDetail}`,
          label: 'Nothing to show',
          detail: fallbackDetail,
          status: 'Clear',
        },
      ];
}

function withFallbackProgressRows(rows, fallbackDetail) {
  return rows.length
    ? rows
    : [{ id: 'empty-progress', label: 'Nothing to show', value: fallbackDetail, percent: 0 }];
}

function isTodayDate(value) {
  if (!value) {
    return false;
  }

  const date = new Date(value);
  const today = new Date();

  if (Number.isNaN(date.getTime())) {
    return false;
  }

  return date.toDateString() === today.toDateString();
}

function isPreReleasedRecord(release) {
  return String(release?.status || '').trim() === 'Pre-Released';
}

function compareDateDesc(firstDate, secondDate) {
  return new Date(secondDate || 0).getTime() - new Date(firstDate || 0).getTime();
}

function formatPurchaseOrderCode(purchaseOrder) {
  return purchaseOrder.poCode || `PO-${String(purchaseOrder.id || '').padStart(4, '0')}`;
}

function getReleaseCode(release) {
  return (
    release.releaseCode ||
    release.release_code ||
    release.releaseNo ||
    release.releaseNumber ||
    `REL-${String(release.id || '').padStart(4, '0')}`
  );
}

function formatQuantity(value, unit = 'sacks') {
  return `${formatWhole(value)} ${unit === 'kg' ? 'kg' : 'sacks'}`;
}

function formatWhole(value) {
  return Number(value || 0).toLocaleString('en-US', {
    maximumFractionDigits: 0,
  });
}

function formatKg(value) {
  return `${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} kg`;
}

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function formatCompactCurrency(value) {
  const numericValue = Number(value || 0);

  if (Math.abs(numericValue) >= 1000000) {
    return `PHP ${(numericValue / 1000000).toLocaleString('en-US', {
      maximumFractionDigits: 2,
    })}M`;
  }

  if (Math.abs(numericValue) >= 1000) {
    return `PHP ${(numericValue / 1000).toLocaleString('en-US', {
      maximumFractionDigits: 1,
    })}K`;
  }

  return formatCurrency(numericValue);
}

export default DashboardPage;
